/**
 * @group Classes
 * 
 * @summary
 * <blockquote>Main logger class for handling log entries with multiple transports</blockquote>
 * 
 * @example
 * ```typescript
 * const logger = new Logger();
 * await logger.info('User signed in', 'users.signin', { userId: 'user-123' });
 * await logger.close();
 * ```
 */
import { LogLevel } from '../types/LogLevel';
import { LogCategory } from '../types/LogCategory';
import { EnvironmentSpec } from '@shared/config';
import { LogEntry } from '../types/LogEntry';
import { ResourceContext } from '../types/ResourceContext';
import { Transport } from '../types/Transport';
import { ConsoleTransport } from '../transports/ConsoleTransport';
import { AxiomTransport } from '../transports/AxiomTransport';
import { getCurrentLogLevel, isResourceEnabled } from '../functions';
import { isProduction, getEnvironment, ENVIRONMENTS } from '@shared/utils';

export class Logger {
  private transports: Transport[] = [];

  constructor() {
    this.initializeTransports();
  }

  private initializeTransports(): void {
    const currentLogLevelResult = getCurrentLogLevel();

    currentLogLevelResult.match({
      ok: (currentLogLevel) => {
        // Initialize console transport
        const consoleTransport = new ConsoleTransport({
          level: currentLogLevel,
          categories: [], // Not used anymore, filtering happens at log level
        });
        this.transports.push(consoleTransport);

        // Initialize Axiom transport if in production
        const productionResult = isProduction();
        productionResult.match({
          ok: (isProd) => {
            if (isProd) {
              const axiomTransport = new AxiomTransport({
                level: currentLogLevel,
                categories: [], // Not used anymore, filtering happens at log level
              });
              this.transports.push(axiomTransport);
            }
          },
          err: (error) => {
            console.warn('Failed to determine production environment, skipping Axiom transport:', error.errorMessage);
          }
        });
      },
      err: (error) => {
        console.error('Failed to get current log level, using default:', error.errorMessage);
        // Fallback to a default log level
        const defaultLogLevel: LogLevel = 'info';
        
        // Initialize console transport with default
        const consoleTransport = new ConsoleTransport({
          level: defaultLogLevel,
          categories: [], // Not used anymore, filtering happens at log level
        });
        this.transports.push(consoleTransport);
      }
    });
  }

  async debug(
    message: string, 
    category: LogCategory, 
    environments?: EnvironmentSpec,
    context?: Record<string, unknown>,
    resourceContext?: ResourceContext
  ): Promise<void> {
    await this.log('debug', message, category, environments, context, resourceContext);
  }

  async info(
    message: string, 
    category: LogCategory, 
    environments?: EnvironmentSpec,
    context?: Record<string, unknown>,
    resourceContext?: ResourceContext
  ): Promise<void> {
    await this.log('info', message, category, environments, context, resourceContext);
  }

  async warn(
    message: string, 
    category: LogCategory, 
    environments?: EnvironmentSpec,
    context?: Record<string, unknown>,
    resourceContext?: ResourceContext
  ): Promise<void> {
    await this.log('warn', message, category, environments, context, resourceContext);
  }

  async error(
    message: string, 
    category: LogCategory, 
    environments?: EnvironmentSpec,
    error?: Error, 
    context?: Record<string, unknown>,
    resourceContext?: ResourceContext
  ): Promise<void> {
    await this.log('error', message, category, environments, context, resourceContext, error);
  }

  // don't remove the eslint-disable-next-line @template/result-pattern, it will fail in vercel but not locally
  // eslint-disable-next-line @template/result-pattern
  private async log(
    level: LogLevel,
    message: string,
    category: LogCategory,
    environments?: EnvironmentSpec,
    context?: Record<string, unknown>,
    resourceContext?: ResourceContext,
    error?: Error
  ): Promise<void> {
    // Check if this resource is enabled for logging in current environment
    if (!isResourceEnabled(category)) {
      return;
    }

    // Check if this log should appear in the current environment based on environments spec
    if (environments) {
      const result = getEnvironment();
      const currentEnv = result.match({
        ok: (env) => env,
        err: () => ENVIRONMENTS.DEVELOPMENT // Default to development if environment detection fails
      });
      const shouldLogInEnv = environments[currentEnv];
      if (shouldLogInEnv === false) {
        return; // Explicitly disabled for this environment
      }
      // If not specified, fall through to resource-based check above
    }



    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      context,
      resourceContext,
      environments,
      error,
    };

    await Promise.all(this.transports.map((transport) => transport.log(entry)));
  }

  async close(): Promise<void> {
    await Promise.all(this.transports.map((transport) => transport.close()));
  }
} 